import { useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { IActivityProductivityRow, ILocationProductivityRow } from '@interfaces/IProductivity';
import type { BreakdownDimension } from '@apis/productivity';
import {
  Building2Icon,
  CadIcon,
  HardHatIcon,
  MapPinIcon,
} from '@utils/icons';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { Card } from '../../shared/Card/Card';
import { PageSizeProvider } from './Table/PageSizeContext';
import { LocationPanel } from './Panels/LocationPanel/LocationPanel';
import { SubcontractorPanel } from './Panels/SubcontractorPanel/SubcontractorPanel';
import { ActivityPanel } from './Panels/ActivityPanel/ActivityPanel';
import { WorkersPanel } from './Panels/WorkersPanel/WorkersPanel';
import { LocationDrillDownDrawer } from './LocationDrillDownDrawer/LocationDrillDownDrawer';
import { AlertDrawerHost } from '@components/Pages/DashboardPage/Cards/AlertDrawerHost';
import { usePrefetchRowSchedules } from './hooks/usePrefetchRowSchedules';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { useProductivityBreakdown } from '../../hooks/useProductivityBreakdown';
import type { IProductivityRange } from '../../utils/productivityRange';
import { BreakdownSkeleton } from './Table/BreakdownSkeleton';
import { ProductivityEmptyState } from '../../shared/ProductivityEmptyState/ProductivityEmptyState';
import styles from './Breakdown.module.css';

interface IBreakdownTab {
  id: BreakdownDimension;
  icon: ComponentType<{ size?: number }>;
  labelKey: string;
  columns: number;
}

// `columns` mirrors each panel's real header count so the loading skeleton
// lays out identically to the loaded table (fade, not reflow).
const TABS: IBreakdownTab[] = [
  { id: 'activities', icon: CadIcon, labelKey: 'productivity.tabs.activities', columns: 8 },
  { id: 'location', icon: MapPinIcon, labelKey: 'productivity.tabs.location', columns: 7 },
  { id: 'subcontractors', icon: Building2Icon, labelKey: 'productivity.tabs.subcontractors', columns: 6 },
  { id: 'workers', icon: HardHatIcon, labelKey: 'productivity.tabs.workers', columns: 4 },
];

interface IBreakdownProps {
  siteId: string | undefined;
  range: IProductivityRange | null;
  active: BreakdownDimension; // The open tab — owned by the page, which sends it with the period request.
  onSelect: (tab: BreakdownDimension) => void;
  periodFetching: boolean; // The period request is on the wire; its response carries this tab's rows.
}

/**
 * Tabbed productivity breakdown. Each tab fetches its dimension lazily on open
 * (cached per dimension); the open tab's rows usually arrive with the period
 * request and are already in that cache. The `location` payload carries its
 * drill-down map, so the row-click drawer needs no extra fetch. An activity
 * row opens its task in place on the shared drawer host (as the dashboard and
 * the Monitor do), in the row's own schedule; mounted only while open.
 */
export function Breakdown({ siteId, range, active, onSelect, periodFetching }: IBreakdownProps) {
  const { t } = useTranslation();
  const [openActivity, setOpenActivity] = useState<{ scheduleNumber: string; taskId: string } | null>(null);
  
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, isLoading, isError, isPlaceholderData } = useProductivityBreakdown(active, siteId, range, periodFetching);
  // The first click should open straight onto the task panel, not behind a cold fetch.
  usePrefetchRowSchedules(siteId, data?.dimension === 'activities' ? data.rows : undefined);

  function selectTab(id: BreakdownDimension) {
    if (id !== active) {
      setDrawerOpen(false);
      setOpenActivity(null);
    }
    onSelect(id);
  }

  function openDrawer(row: ILocationProductivityRow) {
    setSelectedLocationId(row.id);
    setDrawerOpen(true);
  }

  function openActivityRow(row: IActivityProductivityRow) {
    setOpenActivity({ scheduleNumber: row.scheduleNumber, taskId: row.taskId });
  }

  const renderPanel = (): ReactNode => {
    if (!range) return <DataUnavailable message={t('productivity.selectTimeframe')} />;
    if (isError) return <DataUnavailable message={t('productivity.error')} />;
    if (isLoading || !data) {
      return <BreakdownSkeleton columns={TABS.find((tab) => tab.id === active)!.columns} />;
    }
    
    // A tab with no rows says so in words. It can only mean an empty window
    // here — an unmeasurable site never renders the breakdown at all.
    if (data.rows.length === 0) {
      return <ProductivityEmptyState />;
    }

    switch (data.dimension) {
      case 'location':
        return <LocationPanel rows={data.rows} onRowClick={openDrawer} />;
      case 'subcontractors':
        return <SubcontractorPanel rows={data.rows} />;
      case 'activities':
        return <ActivityPanel rows={data.rows} onRowClick={openActivityRow} />;
      case 'workers':
        return <WorkersPanel rows={data.rows} />;
    }
  };

  // A row a refetch dropped closes the drawer rather than linger in it.
  const locations = data?.dimension === 'location' ? data : null;
  const selectedLocation = locations?.rows.find((row) => row.id === selectedLocationId);
  const drillDown = selectedLocationId === null ? undefined : locations?.drillDowns[selectedLocationId];

  return (
    <>
      <div className={styles.wrapper}>
        <ScrollArea
          orientation="horizontal"
          className={styles.tabBar}
          viewportClassName={styles.tabList}
          viewportProps={{ role: 'tablist', 'aria-label': t('productivity.tabs.ariaLabel') }}
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={tab.id === active}
                className={styles.tab}
                onClick={() => selectTab(tab.id)}
              >
                <Icon size={15} />
                {t(tab.labelKey)}
              </button>
            );
          })}
        </ScrollArea>

        <PageSizeProvider>
          <Card refreshing={isPlaceholderData}>{renderPanel()}</Card>
        </PageSizeProvider>
      </div>

      {openActivity && siteId && (
        <AlertDrawerHost
          siteId={siteId}
          itemKey={`act:${openActivity.taskId}`}
          scheduleNumber={openActivity.scheduleNumber}
          onClose={() => setOpenActivity(null)}
        />
      )}

      {selectedLocation && (
        <LocationDrillDownDrawer
          row={selectedLocation}
          drillDown={drillDown}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}
